// Pure helpers of the mail read path (architecture/MAIL.md §3.1, §4) — row
// normalizers, the list-key algebra, membership + upsert reducers and the
// label formatters. PURE on purpose: every rule the list pane and the stream
// handler share lives here once, and the unit suite exercises these directly
// (no DOM, no SDK).
//
// Server shapes (server/src/core/models/MailThread.js / MailMessage.js —
// mirrored, never invented): a thread row carries subject / snippet /
// participants[{ name, email, role }] / lastMessageAt / messageCount /
// unreadCount / attachmentCount / labels[] / folderRoles[] / starred /
// important / snoozedUntil / muted / hasInvite; a message envelope carries
// from/to/cc/bcc/replyTo address lists, sentAt/receivedAt, flags{ read,
// starred, draft, important, pending } and attachments[{ providerAttachmentId,
// filename, mime, size, contentId, inline }].

// The six provider folder roles (MailThread.folderRoles enum) plus the two
// client pseudo-folders the sidebar offers: `starred` (flag view) and
// `label:<id>` (one provider label). `scheduled` arrives with the outbox
// pane, not the thread list.
export const MAIL_FOLDER_ROLES = ['inbox', 'sent', 'drafts', 'archive', 'spam', 'trash']

export const isMailLabelFolder = (folder) => String(folder || '').startsWith('label:')

export const mailFolderLabelId = (folder) =>
  isMailLabelFolder(folder) ? String(folder).slice('label:'.length) : null

// One list slot per (account, folder) pair — `all` is the unified inbox.
export const mailListKey = (accountId, folder) => `${accountId || 'all'}|${folder || 'inbox'}`

export const parseMailListKey = (key) => {
  const raw = String(key || '')
  const at = raw.indexOf('|')
  if (at === -1) return { accountId: null, folder: raw || 'inbox' }
  const account = raw.slice(0, at)
  return {
    accountId: account && account !== 'all' ? account : null,
    folder: raw.slice(at + 1) || 'inbox'
  }
}

export const activeMailListKey = (root) =>
  mailListKey(root && root.mailActiveAccountId, root && root.mailActiveFolder)

// The GET /threads query for the active view (§5.2): the pseudo-folders
// translate to flag filters, the real roles pass through, and the filter bar
// rides on top. Empty/off filters are omitted so the request stays minimal.
export const mailListQuery = (root) => {
  const r = root || {}
  const q = {}
  if (r.mailActiveAccountId) q.accountId = r.mailActiveAccountId
  const folder = r.mailActiveFolder || 'inbox'
  if (folder === 'starred') q.starred = true
  else if (isMailLabelFolder(folder)) q.label = mailFolderLabelId(folder)
  else q.folder = folder
  const f = r.mailFilters || {}
  if (f.unread) q.unread = true
  if (f.starred) q.starred = true
  if (f.attachments) q.attachments = true
  if (f.from) q.from = f.from
  if (f.label && !q.label) q.label = f.label
  const query = String(r.mailSearchQuery || '').trim()
  if (query) q.q = query
  return q
}

const _id = (row) => {
  const v = row && (row.id || row._id)
  return v ? String(v) : null
}

const _iso = (v) => {
  if (!v) return null
  if (typeof v === 'string') return v
  try {
    return new Date(v).toISOString()
  } catch (_) {
    return null
  }
}

const _addr = (a) => ({
  name: (a && a.name) || '',
  email: ((a && a.email) || '').toLowerCase()
})

const _addrList = (list) => (Array.isArray(list) ? list.map(_addr).filter((a) => a.email) : [])

// Server row (REST or `mail.thread.upsert` frame) → the client thread shape.
// Tolerant of `_id` vs `id` and Date vs ISO — the stream merges
// `{ accountId, workspaceId }` on top of the REST row, so `account` may
// arrive under either name.
export const normalizeMailThread = (row) => {
  const r = row || {}
  return {
    id: _id(r),
    account: String(r.account || r.accountId || ''),
    workspace: String(r.workspace || r.workspaceId || ''),
    providerThreadId: r.providerThreadId || null,
    subject: r.subject || '',
    snippet: r.snippet || '',
    participants: Array.isArray(r.participants)
      ? r.participants.map((p) => ({ ..._addr(p), role: (p && p.role) || 'from' }))
      : [],
    firstMessageAt: _iso(r.firstMessageAt),
    lastMessageAt: _iso(r.lastMessageAt),
    messageCount: Number(r.messageCount || 0),
    unreadCount: Number(r.unreadCount || 0),
    attachmentCount: Number(r.attachmentCount || 0),
    labels: Array.isArray(r.labels) ? r.labels.map(String) : [],
    folderRoles: Array.isArray(r.folderRoles) ? r.folderRoles.map(String) : [],
    starred: !!r.starred,
    important: !!r.important,
    snoozedUntil: _iso(r.snoozedUntil),
    muted: !!r.muted,
    hasInvite: !!r.hasInvite,
    links: r.links || {},
    deleted: !!r.deleted
  }
}

// Server message row → the envelope shape the thread view and the compose
// entry points (`replyToMailMessage`) consume. `bodyState` rides through when
// GET /threads/:id serves it ('cached' | 'missing' | …).
export const normalizeMailMessage = (row) => {
  const r = row || {}
  const flags = r.flags || {}
  return {
    id: _id(r),
    account: String(r.account || r.accountId || ''),
    thread: String(r.thread || r.threadId || ''),
    internetMessageId: r.internetMessageId || null,
    inReplyTo: r.inReplyTo || null,
    from: _addr(r.from),
    to: _addrList(r.to),
    cc: _addrList(r.cc),
    bcc: _addrList(r.bcc),
    replyTo: _addrList(r.replyTo),
    subject: r.subject || '',
    sentAt: _iso(r.sentAt),
    receivedAt: _iso(r.receivedAt),
    snippet: r.snippet || '',
    labels: Array.isArray(r.labels) ? r.labels.map(String) : [],
    folderRoles: Array.isArray(r.folderRoles) ? r.folderRoles.map(String) : [],
    flags: {
      read: !!flags.read,
      starred: !!flags.starred,
      draft: !!flags.draft,
      important: !!flags.important,
      pending: !!flags.pending
    },
    attachments: Array.isArray(r.attachments)
      ? r.attachments.map((a) => ({
          id: (a && (a.providerAttachmentId || a.id)) || '',
          filename: (a && a.filename) || '',
          mime: (a && a.mime) || 'application/octet-stream',
          size: Number((a && a.size) || 0),
          contentId: (a && a.contentId) || null,
          inline: !!(a && a.inline)
        }))
      : [],
    hasInvite: !!(r.invite && (r.invite.method || r.invite.uid)),
    bodyState: r.bodyState || null
  }
}

// One tolerant reader for the list page whatever envelope core settles on —
// a bare array, `{ rows, cursor, exhausted }` or `{ threads, nextCursor }`.
// The exact shape is pinned on the server ticket; the UI never breaks on it.
export const normalizeMailThreadPage = (res) => {
  const raw = Array.isArray(res) ? { rows: res } : res && typeof res === 'object' ? res : {}
  const rows = (Array.isArray(raw.rows) ? raw.rows : Array.isArray(raw.threads) ? raw.threads : [])
    .map(normalizeMailThread)
    .filter((t) => t.id)
  const cursor = raw.cursor || raw.nextCursor || null
  return { rows, cursor, exhausted: raw.exhausted === true || !cursor }
}

// ── membership + upsert (the stream reducer) ────────────────────────────────

// Does a thread row belong to a list slot? The pseudo-folders read flags; the
// real roles read folderRoles; a deleted/tombstoned row belongs nowhere.
export const mailThreadInList = (thread, key) => {
  const t = thread || {}
  if (!t.id || t.deleted) return false
  const { accountId, folder } = parseMailListKey(key)
  if (accountId && String(t.account) !== String(accountId)) return false
  if (folder === 'starred') return !!t.starred
  if (isMailLabelFolder(folder)) return t.labels.includes(mailFolderLabelId(folder))
  return t.folderRoles.includes(folder)
}

const _sortRows = (rows) =>
  [...rows].sort((a, b) =>
    String(b.lastMessageAt || '').localeCompare(String(a.lastMessageAt || ''))
  )

// Apply one `mail.thread.upsert` to every loaded list slot: insert where it
// now belongs, update in place, remove where it no longer matches. Pure —
// returns a NEW lists object (only touched slots are copied) or the input
// when nothing changed.
export const applyMailThreadUpsert = (lists, row) => {
  const thread = normalizeMailThread(row)
  if (!thread.id) return lists || {}
  const src = lists || {}
  let changed = false
  const next = {}
  for (const key of Object.keys(src)) {
    const slot = src[key] || {}
    const rows = Array.isArray(slot.rows) ? slot.rows : []
    const at = rows.findIndex((r) => r && r.id === thread.id)
    const belongs = mailThreadInList(thread, key)
    if (belongs && at === -1) {
      next[key] = { ...slot, rows: _sortRows([thread, ...rows]) }
      changed = true
    } else if (belongs && at !== -1) {
      const copy = [...rows]
      copy[at] = thread
      next[key] = { ...slot, rows: _sortRows(copy) }
      changed = true
    } else if (!belongs && at !== -1) {
      next[key] = { ...slot, rows: rows.filter((r) => r && r.id !== thread.id) }
      changed = true
    } else {
      next[key] = slot
    }
  }
  return changed ? next : src
}

export const applyMailThreadDelete = (lists, threadId) => {
  const id = threadId ? String(threadId) : null
  const src = lists || {}
  if (!id) return src
  let changed = false
  const next = {}
  for (const key of Object.keys(src)) {
    const slot = src[key] || {}
    const rows = Array.isArray(slot.rows) ? slot.rows : []
    if (rows.some((r) => r && r.id === id)) {
      next[key] = { ...slot, rows: rows.filter((r) => r && r.id !== id) }
      changed = true
    } else next[key] = slot
  }
  return changed ? next : src
}

// Optimistic flag write: patch the thread wherever it is loaded, WITHOUT
// re-deriving membership (an archive still shows its row until the server's
// own upsert confirms — the action layer removes rows explicitly where the
// gesture demands it, e.g. archive-from-inbox).
export const patchMailThreadEverywhere = (lists, threadId, patch) => {
  const id = threadId ? String(threadId) : null
  const src = lists || {}
  if (!id || !patch) return src
  let changed = false
  const next = {}
  for (const key of Object.keys(src)) {
    const slot = src[key] || {}
    const rows = Array.isArray(slot.rows) ? slot.rows : []
    const at = rows.findIndex((r) => r && r.id === id)
    if (at === -1) {
      next[key] = slot
      continue
    }
    const copy = [...rows]
    copy[at] = { ...copy[at], ...patch }
    next[key] = { ...slot, rows: copy }
    changed = true
  }
  return changed ? next : src
}

export const findMailThread = (lists, threadId) => {
  const id = threadId ? String(threadId) : null
  if (!id) return null
  for (const slot of Object.values(lists || {})) {
    const rows = slot && Array.isArray(slot.rows) ? slot.rows : []
    const hit = rows.find((r) => r && r.id === id)
    if (hit) return hit
  }
  return null
}

// ── labels for the row ──────────────────────────────────────────────────────

// "Nika, Thomas 3" — the senders of a thread, the viewer's own addresses as
// `meLabel`, first names preferred, deduped, message count appended by the
// row itself. Pure: the caller resolves selfAddresses + the localized "me".
export const mailParticipantsLabel = (thread, selfAddresses, meLabel) => {
  const t = thread || {}
  const self = (selfAddresses || []).map((a) => String(a).toLowerCase())
  const me = meLabel || 'me'
  const seen = new Set()
  const names = []
  const list = (t.participants || []).filter((p) => p && p.role === 'from')
  const source = list.length ? list : t.participants || []
  for (const p of source) {
    const email = (p.email || '').toLowerCase()
    if (!email || seen.has(email)) continue
    seen.add(email)
    if (self.includes(email)) names.push(me)
    else names.push((p.name || '').split(' ')[0] || email.split('@')[0])
  }
  return names.join(', ')
}

// Gmail-scale timestamps: today → clock, this year → "5 Sep", else →
// "05/09/24" (locale-formatted). `now` is injectable for the tests.
//
// `format` is the caller's `(date, options) => text`. A component passes
// `(d, o) => el.call('localizedDate', d, o)`: passing the app language as
// `locale` is NOT enough, because Chrome ships no Georgian locale data and
// silently formats `ka` as en-GB (`5 Sept` on a Georgian inbox, live walk
// 2026-09-21). Without `format` the old locale path stays, which the tests use.
export const mailTimeLabel = (iso, locale, now, format) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const ref = now ? new Date(now) : new Date()
  const loc = locale || undefined
  const text = (options, time) =>
    (typeof format === 'function' && format(d, options)) ||
    (time ? d.toLocaleTimeString(loc, options) : d.toLocaleDateString(loc, options))
  if (d.toDateString() === ref.toDateString()) {
    return text({ hour: '2-digit', minute: '2-digit' }, true)
  }
  if (d.getFullYear() === ref.getFullYear()) {
    return text({ day: 'numeric', month: 'short' })
  }
  return text({ day: '2-digit', month: '2-digit', year: '2-digit' })
}

export const mailInitials = (nameOrEmail) => {
  const s = String(nameOrEmail || '').trim()
  if (!s) return '?'
  const at = s.indexOf('@')
  const base = at > 0 ? s.slice(0, at) : s
  const parts = base.split(/[\s._-]+/).filter(Boolean)
  if (!parts.length) return s[0].toUpperCase()
  const first = parts[0][0] || ''
  const last = parts.length > 1 ? parts[parts.length - 1][0] || '' : ''
  return (first + last).toUpperCase() || '?'
}

export const mailAttachmentSizeLabel = (bytes) => {
  const n = Number(bytes || 0)
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// The unread total the sidebar badges read — per account from the stream's
// `mail.unread` frames (mailUnreadCounts), summed for All inboxes.
export const unreadForMailAccount = (root, accountId) => {
  const counts = (root && root.mailUnreadCounts) || {}
  if (accountId) return Number(counts[accountId] || 0)
  return Object.values(counts).reduce((sum, n) => sum + Number(n || 0), 0)
}

// The shell badge number (architecture/MAIL.md §3.6) — inbox unread summed
// over ACCESSIBLE accounts, which is NOT `unreadForMailAccount(root, null)`.
//
// That one sums the raw map, and the map is server-keyed state that OUTLIVES
// an account: revoke a shared-inbox grant and listAccounts stops returning the
// row while its count entry stays behind, so the raw sum keeps a red dot lit
// over an inbox the viewer can no longer open — and clicking it shows nothing.
// The account list is the ACL, so the sum reads through it.
//
// `accounts` is an optional override for the ONE caller that has fresher rows
// than root does: the stream's `mail.snapshot` carries the ACL-filtered
// accounts and the counts in one frame, and derives the total BEFORE that
// frame's own patch lands on root.
export const mailTotalUnread = (root, accounts) => {
  const counts = (root && root.mailUnreadCounts) || {}
  const list = accounts || (root && root.mailAccounts) || []
  let sum = 0
  for (const a of list) {
    if (!a || a.id === undefined || a.id === null) continue
    sum += Number(counts[String(a.id)] || 0)
  }
  return sum
}

// Attach the shell badge signal to a patch that moves either half of the sum
// — the per-account counts or the account list itself (architecture/MAIL.md
// §3.6). `navUnreadCounts.mail` is the ONE key both badge surfaces read:
// AppNavbarButton.UnreadBadge's `unreadFor(s) => root.navUnreadCounts[s.key]`
// and the header envelope's dot (workspace/app.js
// HeaderLeft.HeaderActions.Mail.MailUnreadBadge).
//
// It rides the SAME root.update as the values it derives from. A second write
// would paint the badge one render behind the sidebar pill that mirrors it,
// and the two would visibly disagree while a burst of frames lands.
//
// MERGE, never replace: `navUnreadCounts` is the shell's shared map and chat
// keeps its own count in it under `chat`.
//
// The sum reads THIS PATCH's values first and root's only as the fallback,
// because both callers derive BEFORE their own patch lands: the stream's
// `mail.snapshot` carries the ACL-filtered accounts and the counts in one
// frame while root still holds the previous tenant's list, and
// `loadMailAccounts` is exactly the write that can SHRINK the account set.
export const withMailNavUnread = (root, patch) => {
  const merged = {
    mailUnreadCounts: patch.mailUnreadCounts || (root && root.mailUnreadCounts),
    mailAccounts: patch.mailAccounts || (root && root.mailAccounts)
  }
  patch.navUnreadCounts = {
    ...((root && root.navUnreadCounts) || {}),
    mail: mailTotalUnread(merged)
  }
  return patch
}

// The viewer's own addresses across accessible accounts (reply-all self-drop,
// the "me" row label).
export const mailSelfAddresses = (root) => {
  const out = []
  for (const a of (root && root.mailAccounts) || []) {
    if (a && a.address) out.push(String(a.address).toLowerCase())
    for (const s of (a && a.sendAs) || []) {
      if (s && s.address) out.push(String(s.address).toLowerCase())
    }
  }
  return [...new Set(out)]
}

// ── the sandboxed body document (MailMessageBody) ───────────────────────────

// Known quote containers the fold hides (Gmail / Outlook / RFC blockquote).
const MAIL_QUOTE_SELECTORS =
  'blockquote[type="cite"], .gmail_quote, .gmail_extra, #divRplyFwdMsg, .ms-outlook-mobile-reference-message'

export const mailBodyHasQuote = (html) =>
  /<blockquote[^>]*type=["']?cite|class=["'][^"']*(gmail_quote|gmail_extra)|id=["']?divRplyFwdMsg/i.test(
    String(html || '')
  )

// A CSP source has to be scheme + host and nothing else: it lands inside a
// `content="…"` attribute, where a `"` closes the attribute and a `;` starts
// a new directive. Anything that is not a plain http(s) origin is DROPPED,
// so a bad read makes the policy stricter and never wider.
const _cspOrigin = (value) => {
  const raw = String(value || '').trim()
  if (!raw || /["'; \t\n]/.test(raw)) return ''
  try {
    const u = new URL(raw)
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.origin : ''
  } catch (e) {
    return ''
  }
}

// The body document's own Content-Security-Policy (§5.10). The iframe
// `sandbox` attribute stops SCRIPTS; it does not stop subresource FETCHES,
// and a fetch is exactly what a tracking pixel is — so the policy is the
// half of the containment that decides whether opening a message tells the
// sender it was opened.
//
// `img-src` tracks the consent latch, because the two states mean different
// things. Blocked: `data:` plus our OWN api origin, where MailBodyService
// signs every inline `cid:` image (`?sig=`) — those must paint or every
// message with an embedded logo looks broken. Consented: the sender's host
// joins, https only, because until the §5.2 image proxy lands the client
// swaps `data-mail-src` to the sender's original URL and a policy that did
// not widen would blank the images the viewer just asked for.
export const buildMailBodyCsp = (apiOrigin, showImages) => {
  const origin = _cspOrigin(apiOrigin)
  const img = `data:${origin ? ` ${origin}` : ''}${showImages ? ' https:' : ''}`
  return `default-src 'none'; img-src ${img}; style-src 'unsafe-inline'`
}

// The iframe document for one sanitised body. PURE string builder — the unit
// suite asserts the contract: no scripts ever, `data-mail-src` swaps to `src`
// ONLY under consent, quotes fold by class, links leave through the sandbox
// (`<base target="_blank">`), and the CSP above rides in the head. Colors
// here are raw CSS by necessity: a document boundary carries no
// design-system token — the host paints the white email surface (both
// schemes; mail bodies assume light, exactly as Gmail renders them).
export const buildMailBodySrcdoc = (body, opts) => {
  const b = body || {}
  const o = opts || {}
  let html = String(b.html || '')
  if (!html && b.text) {
    html = `<div style="white-space:pre-wrap">${String(b.text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')}</div>`
  }
  if (o.showImages) html = html.replace(/\bdata-mail-src=/g, 'src=')
  const foldCss = o.showQuoted ? '' : `${MAIL_QUOTE_SELECTORS} { display: none; }`
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    // Before <base> and before any markup — a meta policy only governs what
    // the parser has not reached yet.
    `<meta http-equiv="Content-Security-Policy" content="${buildMailBodyCsp(
      o.apiOrigin,
      o.showImages
    )}">` +
    '<base target="_blank">' +
    '<style>' +
    'body{margin:0;padding:0;font:14px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#1f2328;background:transparent;word-break:break-word;overflow-wrap:anywhere}' +
    'img{max-width:100%;height:auto}' +
    'img.mail-blocked-image{min-width:24px;min-height:24px;background:#f0f1f3;border-radius:4px}' +
    'a{color:#0969da}' +
    'blockquote{margin:8px 0 8px 8px;padding-left:12px;border-left:2px solid #d0d7de;color:#57606a}' +
    'pre{white-space:pre-wrap}' +
    'table{max-width:100%}' +
    foldCss +
    '</style></head><body>' +
    html +
    '</body></html>'
  )
}
